import React from 'react';
import { Form, Formik } from 'formik';
import * as Yup from 'yup';
import { Button, Modal } from '@rabbit/elements/shared-components';
import { ConditionalProfileFields } from '../../molecules/ConditionalProfileFields/ConditionalProfileFields';
import { DRDocType } from '@rabbit/firebase/react';
import { DTConsumer_Private } from '@rabbit/data/types';
import { DR_Consumer_Private, usePortalDocumentEditor } from '@rabbit/data/portal';

type ProfileCompletionModalProps = {
  consumerId: string;
  phoneMissing: boolean;
  ageMissing: boolean;
};

export const ProfileCompletionModal: React.FC<ProfileCompletionModalProps> = ({
  consumerId,
  phoneMissing,
  ageMissing,
}) => {
  const { body, update, commit, isReady } = usePortalDocumentEditor(
    DR_Consumer_Private as DRDocType<DTConsumer_Private>,
    consumerId
  );

  const [saving, setSaving] = React.useState(false);

  if (!body) return null;

  const validationSchema = Yup.object({
    ...(phoneMissing && {
      phone: Yup.string().required('Required'),
    }),
    ...(ageMissing && {
      age_bracket: Yup.string().required('Required'),
    }),
  });

  return (
    <Modal
      kind="generic"
      className="mx-4 max-w-[480px] rounded-2xl"
      settings={{ handleClose: () => {} }}
    >
      {/* Header */}
      <div className="border-b px-6 pt-6 pb-4">
        <h2 className="text-xl font-semibold text-gray-900">
          Complete your details
        </h2>
        <p className="mt-1 text-sm text-gray-600">
          We need a couple of details to finish setting up your account.
        </p>
      </div>

      <Formik
        initialValues={{
          phone: body.phone ?? '',
          age_bracket: body.age_bracket ?? '',
        }}
        validationSchema={validationSchema}
        onSubmit={async (values) => {
          if (!isReady) return;
          setSaving(true);
          try {
            const next: DTConsumer_Private = { ...body };
            if (phoneMissing || values.phone) {
              next.phone = values.phone ?? '';
            }
            if (ageMissing || values.age_bracket) {
              next.age_bracket = values.age_bracket ?? '';
            }
            update(next);
            await commit();
          } finally {
            setSaving(false);
          }
        }}
      >
        {({ values }) => {
          const showPhone = phoneMissing || Boolean(body.phone);
          const showAge = ageMissing || Boolean(body.age_bracket);
          return (
          <Form>
            <div className="px-6 py-5">
              <div className="rounded-xl bg-gray-50 p-4">
                <ConditionalProfileFields
                  showPhone={showPhone}
                  showAgeRange={showAge}
                  containerClassName="flex flex-col gap-5"
                />
              </div>
            </div>

            <div className="px-6 pb-6">
              <Button
                kind="primary"
                type="submit"
                loading={saving || !isReady}
                disabled={
                  (phoneMissing && !values.phone) ||
                  (ageMissing && !values.age_bracket)
                }
                className="h-11 w-full rounded-lg text-base font-medium"
              >
                Save and continue
              </Button>

              <p className="mt-3 text-center text-xs text-gray-500">
                Your information is kept private and secure.
              </p>
            </div>
          </Form>
          );
        }}
      </Formik>
    </Modal>
  );
};

